/**
 * SuperPlans — `/super/plans` — manage standard plans and per-tenant custom plans.
 * Each plan exposes:
 *   • Pricing (monthly / yearly / setup, GST %)
 *   • A feature matrix toggle grid
 *   • Numeric limits (max_stores, max_orders_per_month, free SMS / WhatsApp, storage_gb)
 *   • SMS / WhatsApp per-message rate + storage overage rate
 */
import { useEffect, useState } from "react";
import { Plus, Save, Trash2, Loader2, Edit3, Mic, Calendar, ShoppingBag, Coins, Share2, Palette, MessageCircle, Star, Code } from "lucide-react";
import { api, inr2 } from "../../lib/api";
import { toast } from "sonner";
import { Field, Modal } from "../admin/AdminRestaurants";

const FEATURE_META = {
    voice_search: { label: "Voice Search", icon: Mic },
    calendar_bookings: { label: "Calendar Bookings", icon: Calendar },
    multi_vertical: { label: "Multi-vertical", icon: ShoppingBag },
    coins_loyalty: { label: "Loyalty Coins", icon: Coins },
    referral_program: { label: "Referrals", icon: Share2 },
    custom_theme: { label: "Custom Theme", icon: Palette },
    whatsapp_notifications: { label: "WhatsApp Notif", icon: MessageCircle },
    priority_support: { label: "Priority Support", icon: Star },
    api_access: { label: "API Access", icon: Code },
};

export default function SuperPlans() {
    const [rows, setRows] = useState([]);
    const [edit, setEdit] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/super/plans").then(({ data }) => setRows(data));
    useEffect(() => { load(); }, []);

    const newPlan = () => setEdit({
        code: "", name: "", monthly_price: 0, yearly_price: 0, setup_fee: 0,
        sms_rate: 0.15, whatsapp_rate: 0.40, storage_overage_per_gb: 50,
        gst_percent: 18.0, sort_order: 99, status: "ACTIVE", is_default: false,
        features: { voice_search: false, calendar_bookings: true, multi_vertical: true,
                     coins_loyalty: true, referral_program: true, custom_theme: false,
                     whatsapp_notifications: false, priority_support: false, api_access: false },
        limits: { max_stores: 5, max_products_per_store: 100, max_orders_per_month: 1000,
                   max_admin_users: 2, max_owners: 5, storage_gb: 1.0,
                   free_sms_per_month: 100, free_whatsapp_per_month: 0 },
    });

    const save = async () => {
        setBusy(true);
        try {
            if (edit.id) await api.patch(`/super/plans/${edit.id}`, edit);
            else await api.post("/super/plans", edit);
            toast.success("Saved");
            setEdit(null); load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    const remove = async (p) => {
        if (!confirm(`Delete plan "${p.name}"? (Subscribed tenants will see it archived.)`)) return;
        try { await api.delete(`/super/plans/${p.id}`); toast.success("Removed"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    return (
        <div data-testid="super-plans">
            <div className="flex justify-between items-center flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold">Subscription Plans</h1>
                    <p className="text-sm text-muted-foreground mt-1">{rows.length} plans · features + limits matrix</p>
                </div>
                <button data-testid="new-plan-btn" onClick={newPlan} className="bg-primary text-primary-foreground rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2"><Plus size={16} /> New Plan</button>
            </div>

            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {rows.map((p) => (
                    <div key={p.id} data-testid={`plan-card-${p.code}`} className="bg-card border border-border rounded-2xl p-5">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{p.code}</p>
                                <p className="font-bold text-lg">{p.name}</p>
                            </div>
                            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${p.status === "ACTIVE" ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}>{p.status}</span>
                        </div>
                        <div className="flex gap-1 mt-1">
                            {p.is_custom && <span className="text-[10px] font-bold bg-purple-100 text-purple-700 rounded-full px-2 py-0.5">CUSTOM · {p.tenant_id?.slice(0, 6)}</span>}
                            {p.public_to_tenants === false && <span className="text-[10px] font-bold bg-muted text-muted-foreground rounded-full px-2 py-0.5">PRIVATE</span>}
                        </div>
                        <p className="text-3xl font-extrabold mt-3">{inr2(p.monthly_price)}<span className="text-xs text-muted-foreground font-medium"> /mo</span></p>
                        <p className="text-xs text-muted-foreground">or {inr2(p.yearly_price)}/yr · setup {inr2(p.setup_fee)} · GST {p.gst_percent}%</p>
                        <div className="grid grid-cols-2 gap-1.5 mt-3 text-[11px]">
                            <Limit label="Stores" v={p.limits?.max_stores} />
                            <Limit label="Orders/mo" v={p.limits?.max_orders_per_month} />
                            <Limit label="Free SMS/mo" v={p.limits?.free_sms_per_month} />
                            <Limit label="Free WA/mo" v={p.limits?.free_whatsapp_per_month} />
                            <Limit label="Storage GB" v={p.limits?.storage_gb} />
                            <Limit label="Owners" v={p.limits?.max_owners} />
                        </div>
                        <div className="flex flex-wrap gap-1 mt-3">
                            {Object.entries(p.features || {}).filter(([, v]) => v).map(([k]) => (
                                <span key={k} className="text-[10px] font-bold bg-primary/10 text-primary rounded-full px-2 py-0.5">{FEATURE_META[k]?.label || k}</span>
                            ))}
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-3">SMS ₹{p.sms_rate}/msg · WA ₹{p.whatsapp_rate}/msg · Overage ₹{p.storage_overage_per_gb}/GB</p>
                        <div className="flex gap-2 mt-4">
                            <button data-testid={`edit-plan-${p.code}`} onClick={() => setEdit({ ...p })} className="flex-1 border border-border rounded-xl py-2 text-sm font-bold inline-flex items-center justify-center gap-2"><Edit3 size={14} /> Edit</button>
                            <button data-testid={`del-plan-${p.code}`} onClick={() => remove(p)} className="px-3 rounded-xl text-destructive border border-destructive/30"><Trash2 size={14} /></button>
                        </div>
                    </div>
                ))}
            </div>

            {edit && (
                <Modal title={edit.id ? `Edit · ${edit.name}` : "New Plan"} onClose={() => setEdit(null)}>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Code (slug)"><input data-testid="plan-code" className="inp uppercase" value={edit.code} onChange={(e) => setEdit({ ...edit, code: e.target.value.toUpperCase() })} /></Field>
                        <Field label="Name"><input className="inp" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
                        <Field label="Monthly ₹"><input type="number" className="inp" value={edit.monthly_price} onChange={(e) => setEdit({ ...edit, monthly_price: Number(e.target.value) })} /></Field>
                        <Field label="Yearly ₹"><input type="number" className="inp" value={edit.yearly_price} onChange={(e) => setEdit({ ...edit, yearly_price: Number(e.target.value) })} /></Field>
                        <Field label="Setup fee ₹"><input type="number" className="inp" value={edit.setup_fee} onChange={(e) => setEdit({ ...edit, setup_fee: Number(e.target.value) })} /></Field>
                        <Field label="GST %"><input type="number" className="inp" value={edit.gst_percent} onChange={(e) => setEdit({ ...edit, gst_percent: Number(e.target.value) })} /></Field>
                        <Field label="SMS rate ₹"><input type="number" step="0.01" className="inp" value={edit.sms_rate} onChange={(e) => setEdit({ ...edit, sms_rate: Number(e.target.value) })} /></Field>
                        <Field label="WhatsApp rate ₹"><input type="number" step="0.01" className="inp" value={edit.whatsapp_rate} onChange={(e) => setEdit({ ...edit, whatsapp_rate: Number(e.target.value) })} /></Field>
                        <Field label="Storage overage ₹/GB"><input type="number" className="inp" value={edit.storage_overage_per_gb} onChange={(e) => setEdit({ ...edit, storage_overage_per_gb: Number(e.target.value) })} /></Field>
                        <Field label="Sort order"><input type="number" className="inp" value={edit.sort_order} onChange={(e) => setEdit({ ...edit, sort_order: Number(e.target.value) })} /></Field>
                    </div>

                    <label className="flex items-center gap-2 text-xs cursor-pointer mt-3 bg-muted/40 rounded-xl px-3 py-2">
                        <input data-testid="plan-public" type="checkbox" className="accent-primary w-4 h-4" checked={edit.public_to_tenants !== false} onChange={(e) => setEdit({ ...edit, public_to_tenants: e.target.checked })} />
                        <span className="font-semibold">Visible to all tenants on self-upgrade screen</span>
                        <span className="text-muted-foreground">{edit.is_custom ? "(also requires tenant assignment)" : ""}</span>
                    </label>
                    <label className="flex items-center gap-2 text-xs cursor-pointer mt-2 bg-muted/40 rounded-xl px-3 py-2">
                        <input data-testid="plan-custom" type="checkbox" className="accent-primary w-4 h-4" checked={!!edit.is_custom} onChange={(e) => setEdit({ ...edit, is_custom: e.target.checked })} />
                        <span className="font-semibold">Custom plan — only visible to a specific tenant</span>
                    </label>
                    {edit.is_custom && (
                        <Field full label="Assigned tenant ID"><input data-testid="plan-tenant-id" className="inp font-mono text-xs" value={edit.tenant_id || ""} onChange={(e) => setEdit({ ...edit, tenant_id: e.target.value })} placeholder="Paste tenant UUID" /></Field>
                    )}

                    <p className="mt-5 text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Features</p>
                    <div className="grid grid-cols-2 gap-2 mt-2 max-h-44 overflow-auto pr-1">
                        {Object.keys(FEATURE_META).map((k) => {
                            const M = FEATURE_META[k];
                            return (
                                <label key={k} className="flex items-center gap-2 text-xs bg-muted/40 rounded-xl px-3 py-2 cursor-pointer">
                                    <input data-testid={`feat-${k}`} type="checkbox" className="accent-primary w-4 h-4" checked={!!edit.features?.[k]} onChange={(e) => setEdit({ ...edit, features: { ...edit.features, [k]: e.target.checked } })} />
                                    <M.icon size={13} className="text-muted-foreground" />
                                    <span className="font-semibold">{M.label}</span>
                                </label>
                            );
                        })}
                    </div>

                    <p className="mt-5 text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Limits</p>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                        {Object.entries(edit.limits || {}).map(([k, v]) => (
                            <label key={k} className="flex flex-col gap-1">
                                <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{k.replaceAll("_", " ")}</span>
                                <input data-testid={`limit-${k}`} type="number" className="inp" value={v} onChange={(e) => setEdit({ ...edit, limits: { ...edit.limits, [k]: Number(e.target.value) } })} />
                            </label>
                        ))}
                    </div>

                    <button data-testid="save-plan" onClick={save} disabled={busy || !edit.name || !edit.code} className="mt-5 w-full bg-primary text-primary-foreground rounded-xl py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">{busy && <Loader2 size={16} className="animate-spin" />}<Save size={16} /> Save</button>
                </Modal>
            )}
        </div>
    );
}

function Limit({ label, v }) {
    return (
        <div className="bg-muted/50 rounded-lg px-2 py-1">
            <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">{label}</p>
            <p className="font-bold">{v ?? "—"}</p>
        </div>
    );
}
